// L'ETAPA 6 DEL SSM: ELS CANVIS.
//
// Fora de React i sense cap import tret de `comparacioSSM.js` i `analisisSSM.js`, que tampoc no
// en tenen. Es prova sola: `npm run prova:canvis`.
//
// ── ELS DOS EIXOS, I PER QUÈ SÓN DOS ─────────────────────────────────────────────────────────
//
//     SISTÈMICAMENT DESITJABLE   ho diuen els models: el model conceptual implica aquesta
//                                activitat i el món no la fa
//     CULTURALMENT FACTIBLE      ho diu la cultura: la gent d'aquesta situació ho acceptaria
//
// **Són independents, i aquesta és tota la lliçó de l'etapa.** Un canvi pot ser bona idea i no
// passar mai; un altre pot ser fàcil i no servir de res. Els únics que es duen a l'etapa 7 són
// els que compleixen **totes dues coses**.
//
// I la casella més interessant **no és la dels dos sís**: és la de **desitjable però no factible**.
// Allà no hi ha cap fracàs, hi ha una troballa: diu **què hauria de canviar abans** perquè aquell
// canvi fos possible. Per això aquesta casella demana el «què caldria» i les altres no.
//
// ── LES TRES MENES DE CANVI (Checkland) ──────────────────────────────────────────────────────
//
//     estructura   com s'agrupa la gent, qui depèn de qui, de què respon cadascú
//     procediment  què es fa i en quin ordre: informes, avisos, passos
//     actitud      què s'espera, què es valora, a qui es fa cas
//
// La tercera és **la més difícil de totes i la que més sovint cal**, i per això es demana la mena
// de canvi: dir «canviarem l'actitud» amb un canvi de procediment és el que fa que els canvis
// no arrelin.

import { fileraDe, diferencies } from './comparacioSSM';
import { contextCultural, potJutjarFactibilitat } from './analisisSSM';

export const JUDICIS = ['yes', 'maybe', 'no'];
export const MENES = ['structure', 'procedure', 'attitude'];

// L'identificador d'un candidat que ve d'una diferència. **Estable**: perquè el judici sobrevisqui
// a tornar a l'etapa 5, a reordenar el model conceptual i a tancar el navegador.
export const idDeDiferencia = (d) => `dif:${d.activityId}:${d.clau}`;

// Els candidats: els que surten de la comparació, més els que s'hi hagin afegit a mà.
//
// **La llista no se l'inventa ningú.** Cada diferència de l'etapa 5 hi arriba amb el seu motiu
// escrit; els afegits a mà hi són perquè no tot el que val la pena canviar surt d'un model.
export function candidats(data) {
  const canvis = data?.changes || {};
  const judicis = canvis.judgements || {};
  const manuals = canvis.candidates || [];

  const delsModels = [];
  for (const cm of data?.conceptualModels || []) {
    const mx = (data?.comparison?.matrices || []).find((x) => x.conceptualModelId === cm.id);
    if (!mx) continue;
    for (const d of diferencies(fileraDe(cm, mx.rows || {}))) {
      const id = idDeDiferencia(d);
      delsModels.push({
        id, text: d.name, motiu: d.clau, delModel: true, conceptualModelId: cm.id,
        ...{ desirable: '', feasible: '', mena: '', why: '' },
        ...(judicis[id] || {}),
      });
    }
  }

  const afegits = manuals.map((c) => ({
    ...{ desirable: '', feasible: '', mena: '', why: '' },
    ...c, delModel: false, ...(judicis[c.id] || {}),
  }));

  return [...delsModels, ...afegits];
}

// Judicis que han quedat penjats: la comparació ha canviat i aquella diferència ja no hi és.
//
// Passa i és bo que passi —s'arregla una cosa i deixa de ser una diferència—, però el judici era
// feina. Es diu, com les respostes òrfenes de l'etapa 5.
export function judicisOrfes(data) {
  const vius = new Set(candidats(data).map((c) => c.id));
  return Object.keys(data?.changes?.judgements || {}).filter((id) => !vius.has(id));
}

export const netejaJudicis = (data) => {
  const orfes = new Set(judicisOrfes(data));
  return Object.fromEntries(Object.entries(data?.changes?.judgements || {})
    .filter(([id]) => !orfes.has(id)));
};

// ── LES QUATRE CASELLES ──────────────────────────────────────────────────────────────────────
//
// «maybe» compta com a **no decidit**: un canvi que encara no s'ha jutjat no ha d'acabar a la
// llista dels que es duran a terme perquè ningú no hi ha dit que no.
const si = (v) => v === 'yes';
const no = (v) => v === 'no';

export function casella(c) {
  if (si(c.desirable) && si(c.feasible)) return 'endavant';
  // Desitjable i no factible: la casella que ensenya alguna cosa.
  if (si(c.desirable) && no(c.feasible)) return 'bloquejat';
  if (no(c.desirable) && si(c.feasible)) return 'inutil';
  if (no(c.desirable) && no(c.feasible)) return 'fora';
  return 'pendent';
}

export const perCaselles = (llista) => {
  const fora = { endavant: [], bloquejat: [], inutil: [], fora: [], pendent: [] };
  for (const c of llista) fora[casella(c)].push(c);
  return fora;
};

// El que es duu a l'etapa 7. **Només els dos sís**, i és tota la regla.
export const acordats = (llista) => llista.filter((c) => casella(c) === 'endavant');

// Els avisos, que es diuen i no impedeixen res.
export function avisos(data) {
  const fora = [];
  const llista = candidats(data);
  if (!potJutjarFactibilitat(data?.problemSituation)) fora.push({ clau: 'ssm.cvSenseAnalisis' });
  if (llista.some((c) => casella(c) === 'endavant' && !c.mena)) fora.push({ clau: 'ssm.cvSenseMena' });
  // Un canvi bloquejat sense dir què caldria és mitja troballa: la casella és interessant
  // justament pel que hi hauria de canviar abans.
  const muts = llista.filter((c) => casella(c) === 'bloquejat' && !String(c.why || '').trim());
  if (muts.length) fora.push({ clau: 'ssm.cvBloquejatMut', n: muts.length });
  const orfes = judicisOrfes(data);
  if (orfes.length) fora.push({ clau: 'ssm.cvOrfes', n: orfes.length });
  return fora;
}

// El context contra el qual es jutja la factibilitat. Ve de les Anàlisis 2 i 3, i és el motiu pel
// qual aquelles es van fer abans que aquesta etapa.
export const contraQue = (data) => contextCultural(data?.problemSituation);

const CANVIS = { JUDICIS, MENES, idDeDiferencia, candidats, judicisOrfes, netejaJudicis,
                 casella, perCaselles, acordats, avisos, contraQue };
export default CANVIS;
